import { Pipe, PipeTransform } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';

type InsertLinksTarget = '_blank' | '_self';

/**
 * Inserts HTML links into a string based on a specific syntax.
 * @param value The input string containing link syntax.
 * @param target The target attribute for the links. Defaults to '_blank'.
 * @param classes The CSS classes to apply to the links. Defaults to 'underline'.
 * @returns The transformed string with HTML links.
 */
@Pipe({ name: 'insertLinks', standalone: true })
export class InsertLinksPipe implements PipeTransform {
  transform(value: string | null | undefined, target: InsertLinksTarget = '_blank', classes: string = 'underline'): SafeHtml {
    // Replace links in the value
    return this.replaceLinks(value, target, classes);
  }

  replaceLinks(innerHTML: string | null | undefined, target?: InsertLinksTarget, classes?: string): SafeHtml {
    // If the value is null or undefined, return it as is
    if (!innerHTML) return innerHTML as SafeHtml;

    // RegEx that matches: [LINK_TEXT](LINK_URL)
    const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
    // All the matches of the regex in the value
    const matches = innerHTML.match(regex);

    // If there are no matches, return the original value
    if (!matches) return innerHTML as SafeHtml;

    matches.forEach((match) => {
      // Extract the link text and URL from the match
      const [linkText, linkUrl] = match.slice(1, -1).split('](');
      // If the value, linkText or linkUrl is not defined, skip this match
      if (!innerHTML || !linkText || !linkUrl) return;
      // Create the HTML string for the link
      const linkHtml = `<a class="${classes ?? 'underline'}" href="${linkUrl}" target="${target ?? '_blank'}">${linkText}</a>`;
      // Replace the match in the value with the link HTML
      innerHTML = innerHTML.replace(match, linkHtml);
    });

    // Return the transformed value as SafeHtml
    return innerHTML as SafeHtml;
  }
}
